import type { TypedClient } from '@platform/openapi';
import { isSettled, sha256Hex, type MediaKind, type MediaView } from './media.ts';

export type UploadPhase = 'hashing' | 'uploading' | 'processing';

export type UploadEvents = {
  readonly onPhase: (phase: UploadPhase) => void;
  readonly onProgress: (fraction: number) => void;
};

export type PutFile = (
  target: { readonly url: string; readonly headers: Readonly<Record<string, string>> },
  file: Blob,
  events: { readonly onProgress: (fraction: number) => void; readonly signal: AbortSignal },
) => Promise<void>;

export type Wait = (milliseconds: number, signal: AbortSignal) => Promise<void>;

export type UploaderDependencies = {
  readonly api: TypedClient;
  readonly put: PutFile;
  readonly wait: Wait;
  readonly intervalMs: number;
  readonly maxPolls: number;
};

export class UploadFailedError extends Error {
  constructor(
    readonly reason: string,
    options?: ErrorOptions,
  ) {
    super(reason, options);
    this.name = 'UploadFailedError';
  }
}

export const putWithProgress: PutFile = (target, file, { onProgress, signal }) =>
  new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open('PUT', target.url);
    for (const [name, value] of Object.entries(target.headers)) request.setRequestHeader(name, value);
    request.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) onProgress(event.loaded / event.total);
    });
    request.addEventListener('load', () => {
      if (request.status >= 200 && request.status < 300) resolve();
      else reject(new UploadFailedError('MEDIA_UPLOAD_INCOMPLETE'));
    });
    request.addEventListener('error', () => {
      reject(new UploadFailedError('MEDIA_UPLOAD_INCOMPLETE'));
    });
    signal.addEventListener('abort', () => {
      request.abort();
    });
    request.send(file);
  });

export const waitFor: Wait = (milliseconds, signal) =>
  new Promise((resolve) => {
    const timer = window.setTimeout(resolve, milliseconds);
    signal.addEventListener('abort', () => {
      window.clearTimeout(timer);
      resolve();
    });
  });

export type Polling = {
  readonly wait: Wait;
  readonly intervalMs: number;
  readonly maxPolls: number;
  readonly signal: AbortSignal;
};

export const pollUntil = async <T>(
  read: () => Promise<T>,
  done: (value: T) => boolean,
  polling: Polling,
  reasonOnTimeout: string,
): Promise<T> => {
  for (let poll = 0; poll < polling.maxPolls && !polling.signal.aborted; poll += 1) {
    const value = await read();
    if (done(value)) return value;
    await polling.wait(polling.intervalMs, polling.signal);
  }
  throw new UploadFailedError(reasonOnTimeout);
};

const settledView = async (
  dependencies: UploaderDependencies,
  mediaId: string,
  signal: AbortSignal,
): Promise<MediaView> => {
  const read = async () =>
    (await dependencies.api.get('/admin/media', { query: { id: [mediaId] } })).items[0];
  const view = await pollUntil(
    read,
    (candidate) => candidate !== undefined && isSettled(candidate.status),
    { ...dependencies, signal },
    'MEDIA_PROCESSING_FAILED',
  );
  if (view === undefined) throw new UploadFailedError('MEDIA_PROCESSING_FAILED');
  return view;
};

export const createUploader =
  (dependencies: UploaderDependencies) =>
  async (file: File, kind: MediaKind, events: UploadEvents, signal: AbortSignal): Promise<MediaView> => {
    events.onPhase('hashing');
    const sha256 = await sha256Hex(file);
    const issued = await dependencies.api.post('/admin/media/uploads', {
      body: { kind, contentType: file.type, bytes: file.size, sha256 },
    });
    events.onPhase('uploading');
    await dependencies.put({ url: issued.uploadUrl, headers: issued.uploadHeaders }, file, {
      onProgress: events.onProgress,
      signal,
    });
    await dependencies.api.post('/admin/media/uploads/{id}/status', {
      path: { id: issued.media.id },
      body: { status: 'uploaded' },
    });
    events.onPhase('processing');
    const view = await settledView(dependencies, issued.media.id, signal);
    if (view.status !== 'ready') throw new UploadFailedError(view.rejectionCode ?? 'MEDIA_PROCESSING_FAILED');
    return view;
  };
